<script>
import { deleteMember, getMemberDetail, updateMember } from '@/api/member.js'
import MemberAssetDialog from './MemberAssetDialog.vue'

export default {
  name: 'MemberDetailPage',
  components: { MemberAssetDialog },
  data() {
    return {
      member: null,
      loading: false,
      editMode: false,
      saving: false,
      deleting: false,
      deleteDialog: false,
      assetDialog: false,
      editError: '',
      editForm: this.getInitialEditForm(),
      providerLabelMap: {
        KAKAO: '카카오',
        NAVER: '네이버',
        GOOGLE: '구글',
        APPLE: '애플',
      },
      providerColorMap: {
        KAKAO: 'warning',
        NAVER: 'success',
        GOOGLE: 'error',
        APPLE: 'grey',
      },
    }
  },
  computed: {
    userCode() {
      return this.$route.params.userCode
    },
    krwAsset() {
      return this.member?.assets?.find(a => a.assetType === 'KRW')?.assetAmount ?? 0
    },
    pointAsset() {
      return this.member?.assets?.find(a => a.assetType === 'POINT')?.assetAmount ?? 0
    },
    goldWeightG() {
      return this.member?.metalSummary?.goldWeightG ?? 0
    },
    silverWeightG() {
      return this.member?.metalSummary?.silverWeightG ?? 0
    },
    nameRules() {
      return [
        v => !!v?.trim() || '이름은 필수입니다.',
        v => v.length <= 100 || '이름은 100자 이내로 입력해 주세요.',
      ]
    },
    phoneRules() {
      return [
        v => !v || /^[0-9\-]{0,20}$/.test(v) || '연락처 형식이 올바르지 않습니다.',
      ]
    },
  },
  mounted() {
    this.fetchDetail()
  },
  methods: {
    getInitialEditForm() {
      return {
        name: '',
        phone: '',
        birthDate: '',
        zipCode: '',
        address1: '',
        address2: '',
        branchCode: '',
      }
    },
    async fetchDetail() {
      this.loading = true
      try {
        const res = await getMemberDetail(this.userCode)
        if (res.data?.code === 0) {
          this.member = res.data.data
        } else {
          this.$router.replace('/members')
        }
      } catch (e) {
        console.error('[MemberDetailPage] fetchDetail error:', e)
        this.$router.replace('/members')
      } finally {
        this.loading = false
      }
    },
    enterEditMode() {
      this.editForm = {
        name: this.member?.name || '',
        phone: this.member?.phone || '',
        birthDate: this.member?.birthDate || '',
        zipCode: this.member?.zipCode || '',
        address1: this.member?.address1 || '',
        address2: this.member?.address2 || '',
        branchCode: this.member?.branchCode || '',
      }
      this.editError = ''
      this.editMode = true
    },
    cancelEdit() {
      this.editMode = false
      this.editError = ''
    },
    searchAddress() {
      if (!window.daum?.Postcode) {
        this.editError = '주소 검색 스크립트를 불러오지 못했습니다.'
        return
      }
      new window.daum.Postcode({
        oncomplete: (data) => {
          this.editForm.zipCode = data.zonecode || ''
          this.editForm.address1 = data.roadAddress || data.autoRoadAddress || data.jibunAddress || ''
        },
      }).open()
    },
    async handleSave() {
      const { valid } = await this.$refs.editForm.validate()
      if (!valid) return

      this.saving = true
      this.editError = ''
      try {
        const res = await updateMember(this.userCode, {
          name: this.editForm.name,
          phone: this.editForm.phone || undefined,
          birthDate: this.editForm.birthDate || undefined,
          zipCode: this.editForm.zipCode || undefined,
          address1: this.editForm.address1 || undefined,
          address2: this.editForm.address2 || undefined,
          branchCode: this.editForm.branchCode || undefined,
        })
        if (res.data?.code === 0) {
          this.member = res.data.data
          this.editMode = false
        } else {
          this.editError = res.data?.message || '회원정보 수정에 실패했습니다.'
        }
      } catch (e) {
        console.error('[MemberDetailPage] handleSave error:', e)
        this.editError = e?.response?.data?.message || '서버 오류가 발생했습니다.'
      } finally {
        this.saving = false
      }
    },
    async handleDelete() {
      this.deleting = true
      try {
        const res = await deleteMember(this.userCode)
        if (res.data?.code === 0) {
          this.deleteDialog = false
          this.$router.push('/members')
        } else {
          this.deleteDialog = false
          alert(res.data?.message || '삭제에 실패했습니다.')
        }
      } catch (e) {
        console.error('[MemberDetailPage] handleDelete error:', e)
        alert(e?.response?.data?.message || '서버 오류가 발생했습니다.')
      } finally {
        this.deleting = false
      }
    },
    formatNumber(num) {
      return Number(num || 0).toLocaleString('ko-KR')
    },
    formatDate(dt) {
      if (!dt) return '-'
      return String(dt).substring(0, 10)
    },
    formatDt(dt) {
      if (!dt) return '-'
      return String(dt).substring(0, 16).replace('T', ' ')
    },
    getAddress(member) {
      const address = [member?.address1, member?.address2].filter(Boolean).join(' ')
      return address || '-'
    },
  },
}
</script>

<template>
  <div>
    <div class="d-flex align-center justify-space-between mb-5">
      <div>
        <div class="d-flex align-center ga-1 text-caption text-medium-emphasis mb-1">
          <span class="breadcrumb-link text-primary" @click="$router.push('/members')">회원관리</span>
          <v-icon size="12">mdi-chevron-right</v-icon>
          <span>상세</span>
        </div>
        <div class="text-h5 font-weight-bold">회원 상세</div>
        <div class="text-body-2 text-grey mt-1">회원 정보 조회 및 수정</div>
      </div>

      <div v-if="!editMode && member" class="d-flex ga-2">
        <v-btn variant="tonal" color="primary" rounded="lg" prepend-icon="mdi-pencil-outline" :disabled="member.deleteYn === 'Y'" @click="enterEditMode">
          수정
        </v-btn>
        <v-btn variant="tonal" color="error" rounded="lg" prepend-icon="mdi-account-remove-outline" :disabled="member.deleteYn === 'Y'" @click="deleteDialog = true">
          회원 삭제
        </v-btn>
      </div>

      <div v-if="editMode" class="d-flex ga-2">
        <v-btn variant="text" :disabled="saving" @click="cancelEdit">취소</v-btn>
        <v-btn color="primary" variant="flat" rounded="lg" :loading="saving" prepend-icon="mdi-check" @click="handleSave">
          저장
        </v-btn>
      </div>
    </div>

    <v-row v-if="loading">
      <v-col v-for="i in 4" :key="i" cols="12" md="6">
        <v-skeleton-loader type="card" />
      </v-col>
    </v-row>

    <template v-if="!loading && member">
      <v-row>
        <v-col cols="12" md="7">
          <v-card rounded="xl" elevation="0" class="detail-card" height="100%">
            <div class="detail-card-header d-flex align-center ga-2 px-5 py-4">
              <div class="header-icon-wrap bg-primary-soft">
                <v-icon color="primary" size="18">mdi-account-outline</v-icon>
              </div>
              <span class="text-subtitle-2 font-weight-bold">기본 정보</span>
              <v-spacer />
              <v-chip v-if="member.deleteYn === 'Y'" size="x-small" color="error" label variant="flat" class="font-weight-bold">
                탈퇴
              </v-chip>
            </div>
            <v-divider />

            <v-card-text v-if="editMode" class="pa-5">
              <v-form ref="editForm">
                <v-row dense>
                  <v-col cols="12" md="6">
                    <v-text-field v-model="editForm.name" label="이름" variant="outlined" density="comfortable" rounded="lg" :rules="nameRules" />
                  </v-col>
                  <v-col cols="12" md="6">
                    <v-text-field v-model="editForm.phone" label="연락처" variant="outlined" density="comfortable" rounded="lg" :rules="phoneRules" />
                  </v-col>
                  <v-col cols="12" md="6">
                    <v-text-field v-model="editForm.birthDate" label="생년월일" type="date" variant="outlined" density="comfortable" rounded="lg" />
                  </v-col>
                  <v-col cols="12" md="6">
                    <v-text-field v-model="editForm.branchCode" label="추천지점코드" variant="outlined" density="comfortable" rounded="lg" placeholder="비우면 해제됩니다." />
                  </v-col>
                  <v-col cols="12" md="4">
                    <v-text-field v-model="editForm.zipCode" label="우편번호" variant="outlined" density="comfortable" rounded="lg" readonly />
                  </v-col>
                  <v-col cols="12" md="8" class="d-flex align-start ga-2">
                    <v-text-field v-model="editForm.address1" label="주소" variant="outlined" density="comfortable" rounded="lg" readonly class="flex-grow-1" />
                    <v-btn height="48" variant="tonal" color="primary" prepend-icon="mdi-map-search-outline" @click="searchAddress">주소 찾기</v-btn>
                  </v-col>
                  <v-col cols="12">
                    <v-text-field v-model="editForm.address2" label="상세 주소" variant="outlined" density="comfortable" rounded="lg" />
                  </v-col>
                </v-row>
                <v-alert v-if="editError" type="error" variant="tonal" density="compact" rounded="lg" class="mt-2">
                  {{ editError }}
                </v-alert>
              </v-form>
            </v-card-text>

            <v-card-text v-else class="pa-5">
              <v-alert v-if="member.deleteYn === 'Y'" type="warning" variant="tonal" density="compact" rounded="lg" class="mb-4">
                탈퇴된 회원입니다. ({{ formatDate(member.deleteDt) }})
              </v-alert>
              <div class="dl-grid">
                <div class="dl-item">
                  <div class="dl-label">회원코드</div>
                  <div class="dl-value text-caption text-medium-emphasis">{{ member.userCode }}</div>
                </div>
                <div class="dl-item">
                  <div class="dl-label">이름</div>
                  <div class="dl-value font-weight-bold">{{ member.name || '-' }}</div>
                </div>
                <div class="dl-item">
                  <div class="dl-label">이메일</div>
                  <div class="dl-value">{{ member.siteEmail || member.email || '-' }}</div>
                </div>
                <div class="dl-item">
                  <div class="dl-label">연락처</div>
                  <div class="dl-value">{{ member.phone || '-' }}</div>
                </div>
                <div class="dl-item">
                  <div class="dl-label">생년월일</div>
                  <div class="dl-value">{{ member.birthDate || '-' }}</div>
                </div>
                <div class="dl-item">
                  <div class="dl-label">주소</div>
                  <div class="dl-value">{{ getAddress(member) }}</div>
                </div>
                <div class="dl-item">
                  <div class="dl-label">추천지점</div>
                  <div class="dl-value">{{ member.branchName && member.branchName !== '-' ? `${member.branchName} (${member.branchCode})` : '-' }}</div>
                </div>
                <div class="dl-item">
                  <div class="dl-label">가입일</div>
                  <div class="dl-value text-medium-emphasis">{{ formatDt(member.insertDt) }}</div>
                </div>
                <div class="dl-item">
                  <div class="dl-label">최종수정</div>
                  <div class="dl-value text-medium-emphasis">{{ formatDt(member.updateDt) }}</div>
                </div>
              </div>
            </v-card-text>
          </v-card>
        </v-col>

        <v-col cols="12" md="5">
          <v-card rounded="xl" elevation="0" class="detail-card mb-4">
            <div class="detail-card-header d-flex align-center ga-2 px-5 py-4">
              <div class="header-icon-wrap bg-indigo-soft">
                <v-icon color="indigo" size="18">mdi-login-variant</v-icon>
              </div>
              <span class="text-subtitle-2 font-weight-bold">로그인 정보</span>
            </div>
            <v-divider />
            <v-card-text class="pa-5">
              <div class="login-section-label">사이트 로그인</div>
              <div class="mb-4">
                <v-chip v-if="member.siteEmail" color="primary" size="small" variant="tonal" label>{{ member.siteEmail }}</v-chip>
                <v-chip v-else color="grey" size="small" variant="tonal" label>미연동</v-chip>
              </div>

              <div class="login-section-label">소셜 로그인</div>
              <div class="d-flex flex-wrap ga-2">
                <v-chip
                  v-for="oauth in (member.oauthList || [])"
                  :key="oauth.provider"
                  :color="providerColorMap[oauth.provider] || 'default'"
                  size="small"
                  variant="tonal"
                  label
                >
                  {{ providerLabelMap[oauth.provider] || oauth.provider }}
                </v-chip>
                <v-chip v-if="!member.oauthList || member.oauthList.length === 0" color="grey" size="small" variant="tonal" label>미연동</v-chip>
              </div>
            </v-card-text>
          </v-card>

          <v-card rounded="xl" elevation="0" class="detail-card">
            <div class="detail-card-header d-flex align-center ga-2 px-5 py-4">
              <div class="header-icon-wrap bg-amber-soft">
                <v-icon color="amber-darken-2" size="18">mdi-wallet-outline</v-icon>
              </div>
              <span class="text-subtitle-2 font-weight-bold">보유 자산</span>
              <v-spacer />
              <v-btn size="small" variant="tonal" color="amber-darken-2" rounded="lg" prepend-icon="mdi-pencil-plus-outline" :disabled="member.deleteYn === 'Y'" @click="assetDialog = true">
                자산입력
              </v-btn>
            </div>
            <v-divider />
            <v-card-text class="pa-5">
              <div class="asset-grid">
                <div class="asset-card asset-card--gold">
                  <div class="asset-label">금 자산</div>
                  <div class="asset-amount asset-amount--gold">{{ goldWeightG }}<span class="asset-unit">g</span></div>
                </div>
                <div class="asset-card asset-card--blue">
                  <div class="asset-label">은 자산</div>
                  <div class="asset-amount asset-amount--silver">{{ silverWeightG }}<span class="asset-unit">g</span></div>
                </div>
                <div class="asset-card asset-card--gold">
                  <div class="asset-label">보유 현금</div>
                  <div class="asset-amount text-primary">{{ formatNumber(krwAsset) }}<span class="asset-unit">원</span></div>
                </div>
                <div class="asset-card asset-card--blue">
                  <div class="asset-label">보유 포인트</div>
                  <div class="asset-amount text-blue-darken-1">{{ formatNumber(pointAsset) }}<span class="asset-unit">P</span></div>
                </div>
              </div>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>
    </template>

    <MemberAssetDialog
      v-if="member"
      v-model="assetDialog"
      :user-code="userCode"
      :current-krw="krwAsset"
      :current-point="pointAsset"
      @updated="fetchDetail"
    />

    <v-dialog v-model="deleteDialog" max-width="400">
      <v-card rounded="xl" elevation="4">
        <div class="px-6 pt-5 pb-4 d-flex align-center ga-2">
          <div class="header-icon-wrap bg-error-soft">
            <v-icon color="error" size="18">mdi-alert-circle-outline</v-icon>
          </div>
          <span class="text-subtitle-1 font-weight-bold">회원 삭제 확인</span>
        </div>
        <v-divider />
        <v-card-text class="pa-6">
          <div class="text-body-2 mb-2">
            <strong class="text-error">{{ member?.name }}</strong> 회원을 삭제하시겠습니까?
          </div>
          <div class="text-caption text-medium-emphasis">
            삭제 후에도 데이터는 유지되며, 관리자 페이지에서 탈퇴 처리된 회원으로 표시됩니다.
          </div>
        </v-card-text>
        <v-divider />
        <v-card-actions class="pa-4">
          <v-spacer />
          <v-btn variant="text" :disabled="deleting" @click="deleteDialog = false">취소</v-btn>
          <v-btn color="error" variant="flat" rounded="lg" :loading="deleting" prepend-icon="mdi-delete-outline" @click="handleDelete">
            삭제
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<style scoped>
.detail-card {
  border: 1px solid rgba(201, 169, 98, 0.16) !important;
}

.detail-card-header {
  background: rgba(201, 169, 98, 0.05);
}

.header-icon-wrap {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border-radius: 8px;
  flex-shrink: 0;
}

.bg-primary-soft { background: rgba(var(--v-theme-primary), 0.1); }
.bg-indigo-soft { background: rgba(63, 81, 181, 0.1); }
.bg-amber-soft { background: rgba(255, 193, 7, 0.12); }
.bg-error-soft { background: rgba(var(--v-theme-error), 0.1); }

.breadcrumb-link {
  cursor: pointer;
}

.breadcrumb-link:hover {
  text-decoration: underline;
}

.dl-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 20px 16px;
}

.dl-item {
  display: flex;
  flex-direction: column;
  gap: 5px;
}

.dl-label,
.login-section-label,
.asset-label {
  font-size: 0.72rem;
  font-weight: 700;
  letter-spacing: 0.04em;
  color: rgba(201, 181, 139, 0.75);
}

.dl-value {
  font-size: 0.875rem;
  color: #efe5cf;
  line-height: 1.45;
}

.asset-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 12px;
}

.asset-card {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 22px 14px;
  border-radius: 16px;
  text-align: center;
  min-height: 120px;
}

.asset-card--gold {
  background: linear-gradient(145deg, rgba(205, 178, 122, 0.15), rgba(205, 178, 122, 0.04));
  border: 1px solid rgba(205, 178, 122, 0.35);
}

.asset-card--blue {
  background: linear-gradient(145deg, rgba(33, 150, 243, 0.1), rgba(33, 150, 243, 0.03));
  border: 1px solid rgba(33, 150, 243, 0.22);
}

.asset-amount {
  font-size: 1.35rem;
  font-weight: 800;
  line-height: 1.2;
}

.asset-unit {
  font-size: 0.8rem;
  font-weight: 500;
  margin-left: 2px;
}

.asset-amount--gold { color: #e2c17a; }
.asset-amount--silver { color: #9dc7ff; }
</style>
